import React, { useEffect, useRef, useState } from 'react';

/**
 * Horizontally scrolling row that fades only the edges with hidden content.
 * At rest: fade on the RIGHT (thumb-side) hinting more content off-screen.
 * Once the user has scrolled: fade appears on the LEFT too. Once they hit
 * the end, the RIGHT fade disappears.
 */
export default function ScrollFadeRow({
  children,
  className = '',
  as: Tag = 'div',
  testId,
}) {
  const ref = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true); // true until we measure

  const measure = () => {
    const el = ref.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 1) {
      setAtStart(true);
      setAtEnd(true);
      return;
    }
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft >= maxScroll - 1);
  };

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el) return;
    const onScroll = () => measure();
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-measure after children swap in
  useEffect(() => { measure(); });

  return (
    <Tag
      ref={ref}
      className={`scroll-fade-x overflow-x-auto no-scrollbar ${className}`}
      data-at-start={atStart}
      data-at-end={atEnd}
      data-testid={testId}
    >
      {children}
    </Tag>
  );
}
